import { feedbackReplys, getFeedbackWebDetailsAisee } from '@/api';
import { toDisplayPlainText } from '@/util/text/unescape';

import type { CustomerSessionPreview } from './sessionPath';

interface ReplyRow {
  id?: unknown;
  content?: unknown;
}

export interface CustomerCardCache {
  titles: Map<string, Promise<string>>;
  replies: Map<string, Promise<Map<string, string>>>;
}

export const createCustomerCardCache = (): CustomerCardCache => ({
  titles: new Map(),
  replies: new Map(),
});

const textOf = (value: unknown) => toDisplayPlainText(value).trim();

const loadTitle = async (sessionId: string) => {
  try {
    const res = (await getFeedbackWebDetailsAisee(sessionId)) as {
      details?: { msg_info?: { message?: unknown } };
    };
    return textOf(res?.details?.msg_info?.message);
  } catch {
    return '';
  }
};

const loadReplies = async (sessionId: string, appId: string) => {
  const contents = new Map<string, string>();
  try {
    const res = (await feedbackReplys({
      fid: sessionId,
      app_id: appId,
    })) as { replys?: ReplyRow[] };
    (res?.replys ?? []).forEach((row) => {
      const id =
        row?.id === undefined || row?.id === null ? '' : String(row.id);
      const content =
        typeof row?.content === 'string' ? row.content.trim() : '';
      if (id && content) contents.set(id, content);
    });
  } catch {
    return contents;
  }
  return contents;
};

const cachedTitle = (sessionId: string, cache: CustomerCardCache) => {
  let pending = cache.titles.get(sessionId);
  if (!pending) {
    pending = loadTitle(sessionId);
    cache.titles.set(sessionId, pending);
  }
  return pending;
};

const cachedReplies = (
  sessionId: string,
  appId: string,
  cache: CustomerCardCache
) => {
  let pending = cache.replies.get(sessionId);
  if (!pending) {
    pending = loadReplies(sessionId, appId);
    cache.replies.set(sessionId, pending);
  }
  return pending;
};

/** 缺标题或定位消息正文时，按客服会话 id 去详情和回复列表补。同一会话只查一次。 */
export const fillCustomerSessionPreview = async (
  preview: CustomerSessionPreview,
  appId: string,
  cache: CustomerCardCache
): Promise<CustomerSessionPreview> => {
  const needsTitle = !preview.title?.trim();
  const focusId = preview.focusMessageId;
  const needsContent = !!focusId && !preview.focusContent?.trim();
  if (!needsTitle && !needsContent) return preview;

  const [title, replies] = await Promise.all([
    needsTitle ? cachedTitle(preview.sessionId, cache) : Promise.resolve(''),
    needsContent
      ? cachedReplies(preview.sessionId, appId, cache)
      : Promise.resolve(undefined),
  ]);

  const next: CustomerSessionPreview = { sessionId: preview.sessionId };
  const resolvedTitle = (needsTitle ? title : preview.title)?.trim();
  if (resolvedTitle) next.title = resolvedTitle;
  if (focusId) {
    next.focusMessageId = focusId;
    const fromList = replies?.get(focusId)?.trim();
    const resolvedContent = (
      needsContent ? fromList : preview.focusContent
    )?.trim();
    if (resolvedContent) next.focusContent = resolvedContent;
  }
  return next;
};
